import { Box } from '@mui/material';
import { ArrowBackIcon, SaveIcon } from 'components/common/Icon';
import { HubspotService } from 'services';
import { IAccount, ProgressTrackerStatus } from 'services/account';
import { UserInfo } from 'models/user';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FormationsTertiaryButton,
  FormationsGhostButton,
} from 'components/common/Buttons';
import { ALLOWED_BANKS } from 'enums';
import { useUpdateCompany } from 'hooks/api';
import { Business } from 'models/business';
import { useUpdateTimeContactProperties } from 'hooks';
import { queryClient } from 'states/reactQueryClient';
import { HaveAccountWithBIB } from './HaveAccountWithBIB';
import { SelectedBank } from './SelectedBank';

type TFormData = {
  haveAccountWith: string;
};

const toFormData = (company: Business) => ({
  haveAccountWith: !company?.useExistingBank ? company?.bankName ?? '' : '',
});

interface TParams {
  account: IAccount;
  company: Partial<Business>;
  handleShowRelayBankCreation: () => void;
  handleComplete: (status: ProgressTrackerStatus, eventData: Object) => void;
  user: Partial<UserInfo>;
}

export const BankSelectionBIB = ({
  account,
  company,
  handleShowRelayBankCreation,
  handleComplete,
}: TParams) => {
  const methods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: toFormData(company as Business),
    shouldUnregister: false,
  });

  const { mutateAsync: updateCompany, isLoading: isCompanyUpdating } =
    useUpdateCompany({
      onSuccess: () => {
        queryClient.invalidateQueries(['business', company?.id]);
      },
    });

  const { mutate: updateTimeContactProperties, isLoading: isContactUpdating } =
    useUpdateTimeContactProperties();

  const updateBankSelection = async (formData: TFormData) => {
    if (company?.id && account?.hubspotContactId) {
      const { haveAccountWith } = formData;
      const bank = haveAccountWith;
      const isExistingBank = !!ALLOWED_BANKS[bank];
      const businessData = {
        bankName: bank,
        useOtherBank: !isExistingBank,
        useExistingBank: isExistingBank,
        hasBankAccount: true,
      };
      await updateCompany({ id: company.id, data: businessData });
      updateTimeContactProperties({
        contactId: account.hubspotContactId,
        property: HubspotService.TimestampAccountType.BANKPREFERENCE,
      });
      handleComplete(ProgressTrackerStatus.Completed, {
        bankName: bank,
      });
    }
  };

  const handleNext = async (data: any) => {
    await updateBankSelection(data);
  };

  return (
    <div data-testid="step-bank-selection">
      {company?.bankName ? (
        <SelectedBank company={company as Business} />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleNext)} noValidate>
            <HaveAccountWithBIB />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '48px',
              }}
            >
              <FormationsGhostButton
                onClick={handleShowRelayBankCreation}
                startIcon={<ArrowBackIcon />}
                data-testid="back-btn"
              >
                Back
              </FormationsGhostButton>
              <FormationsTertiaryButton
                isLoading={isCompanyUpdating || isContactUpdating}
                type="submit"
                size="large"
                data-testid="continue"
                loadingPosition="end"
                endIcon={<SaveIcon />}
                sx={{ ml: 'auto' }}
              >
                Save and Continue
              </FormationsTertiaryButton>
            </Box>
          </form>
        </FormProvider>
      )}
    </div>
  );
};
